import { chunk, omit } from 'lodash'
import {
    parseDateString, parseTimeString, validation,
} from 'utils'
import * as Yup from 'yup'
import { setProgress } from 'features/export_import/importExportSlice'
import {
    convertBooleanValue,
    convertDateTimeForSubmitting,
    LIMITED_NUMBER_OF_UPLOADED_ITEMS,
} from 'features/export_import/utils'
import { showErrorNotification } from 'features/notifications/notificationsSlice'
// @ts-ignore
import SampleFile from 'features/export_import/sample_files/sample_meeting.xlsx'
import {
    ImportDataFormProps, MappedField, SampleDataType
} from 'features/export_import/types'
import { importMeetings } from '../graphql/mutation'

export const mappedFields: MappedField[] = [
    {
        field: 'externalId', label: 'ID', isRequired: true
    },
    {
        field: 'title', label: 'Title', isRequired: true
    },
    {
        field: 'startDate', label: 'Start Date', isRequired: true
    },
    {
        field: 'startTime', label: 'Start Time', isRequired: true
    },
    {
        field: 'endDate', label: 'End Date', isRequired: true
    },
    {
        field: 'endTime', label: 'End Time', isRequired: true
    },
    { field: 'description', label: 'Description' },
    { field: 'closed', label: 'Closed' },
    { field: 'capacity', label: 'Capacity' },
    { field: 'enableBreakoutRooms', label: 'Enable breakout rooms' },
    { field: 'isWalkInMeeting', label: 'Walk in meeting' }
]

export const sampleData: SampleDataType[] = [
    { header: 'ID', value: 'Unique ID' },
    { header: 'Title', value: 'title' },
    { header: 'Start Date', value: 'DD/MM/YYYY' },
    { header: 'Start Time', value: 'HH:mm' },
    { header: 'End Date', value: 'DD/MM/YYYY' },
    { header: 'End Time', value: 'HH:mm' },
    { header: 'Description', value: 'description' },
    { header: 'Closed', value: '0 or 1' },
    { header: 'Capacity', value: '25' },
    { header: 'Enable breakout rooms', value: '0 or 1' },
    { header: 'Walk in meeting', value: '0 or 1' },
]

export const validationSchema = Yup.array().of(
    Yup.object().shape({
        externalId: validation.excelExternalId,
        title: Yup.string().required(),
        startDate: Yup.date().typeError('Invalid date').transform(parseDateString).required(),
        startTime: Yup.date().typeError('Invalid time').transform(parseTimeString).required(),
        endDate: Yup.date().typeError('Invalid date').transform(parseDateString).required(),
        endTime: Yup.date().typeError('Invalid time').transform(parseTimeString).required()
            .when(
                ['startDate', 'startTime', 'endDate'],
                // @ts-ignore
                (startDate, startTime, endDate, schema) => schema.test(
                    'endDateGreaterThanStartDate',
                    'EndDate must be greater than startDate',
                    (v) => {
                        let data = {
                            startDate, startTime, endDate, endTime: v
                        }
                        data = convertDateTimeForSubmitting(data)
                        return data.endDate > data.startDate
                    }
                )
            ),
        enableBreakoutRooms: validation.excelBoolean,
        isWalkInMeeting: validation.excelBoolean,
        closed: validation.excelBoolean,
        capacity: Yup.number().typeError('Invalid number').min(0),
    })
)

export const importProps: Omit<ImportDataFormProps, 'onSubmit'> = {
    mappedFields,
    sampleData,
    sampleFile: SampleFile,
    validationSchema,
}

export const handleSubmit = async (meetingSpace, data, dispatch, callback) => {
    const submittedData = data.map((e) => {
        e = convertDateTimeForSubmitting(e)

        return omit({
            ...e,
            startsAt: e.startDate,
            endsAt: e.endDate,
            enableBreakoutRooms: convertBooleanValue(e.enableBreakoutRooms),
            isWalkInMeeting: convertBooleanValue(e.isWalkInMeeting),
            closed: convertBooleanValue(e.closed),
            capacity: parseInt(e.capacity, 10) || 0
        }, ['startDate', 'endDate'])
    })

    let completed = 0
    let errorItems = []
    const chunked_data = chunk(submittedData, LIMITED_NUMBER_OF_UPLOADED_ITEMS)
    // eslint-disable-next-line no-restricted-syntax
    for (const _data of chunked_data) {
        // eslint-disable-next-line no-await-in-loop
        const response = await importMeetings({ meetingSpace, data: _data })
        if (response.errors.length) {
            return dispatch(showErrorNotification(response.errors))
        }
        const importResult = response.data.importMeeting
        completed += LIMITED_NUMBER_OF_UPLOADED_ITEMS
        errorItems = [...errorItems, ...importResult.errorItems.map((e) => `${e.id} (${e.message})`)]
        dispatch(
            setProgress({
                completed: Math.min(completed, submittedData.length),
                errorItems,
            })
        )
    }
    callback?.()
}
